"use client";

import type { ComponentType } from "react";
import { cn } from "../../../lib/utils";
import type { FooterPaymentMethod } from "../footer.types";
import {
  FooterAmexBadge,
  FooterKlarnaBadge,
  FooterMastercardBadge,
  FooterPaypalBadge,
  FooterVisaBadge,
} from "./footer-payment-badges";

const PAYMENT_METHODS: Record<
  FooterPaymentMethod,
  { label: string; Badge: ComponentType; hasShadow: boolean }
> = {
  // Visa and Klarna draw their shadow themselves.
  visa: { label: "Visa", Badge: FooterVisaBadge, hasShadow: false },
  paypal: { label: "PayPal", Badge: FooterPaypalBadge, hasShadow: true },
  klarna: { label: "Klarna", Badge: FooterKlarnaBadge, hasShadow: false },
  mastercard: {
    label: "Mastercard",
    Badge: FooterMastercardBadge,
    hasShadow: true,
  },
  // Amex draws its own card and shadow, like Visa and Klarna.
  amex: { label: "American Express", Badge: FooterAmexBadge, hasShadow: false },
};

export interface FooterPaymentMethodsProps {
  methods: FooterPaymentMethod[];
  /** Accessible name of the list, e.g. "Payment methods". */
  label: string;
}

/** Badges of the accepted payment methods; larger on mobile, as designed. */
export function FooterPaymentMethods(
  props: Readonly<FooterPaymentMethodsProps>
) {
  return (
    <ul
      aria-label={props.label}
      className="flex flex-wrap justify-center gap-[23px] lg:gap-4"
    >
      {props.methods.map((method) => {
        const { label, Badge, hasShadow } = PAYMENT_METHODS[method];

        return (
          <li key={method}>
            <span
              aria-label={label}
              className={cn(
                "relative block aspect-[34.2305/23.8125] w-[49px] lg:w-[34px]",
                hasShadow &&
                  "drop-shadow-[0_0_8px_rgba(0,0,0,0.07)] lg:drop-shadow-[0_0_5.625px_rgba(0,0,0,0.07)]"
              )}
              role="img"
            >
              <Badge />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
